import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Card,
	Col,
	Descriptions,
	Flex,
	Row,
	Select,
	Space,
	Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useQuery } from 'react-query';
import {
	getUtcOffset,
	toMoment,
} from '@shared/utils/date';
import {
	PortActionTypes,
	TimeZoneFormat,
	TimeZoneMode,
} from '@shared/utils/constants';
import { round } from '@shared/utils/math';
import {
	formatAndRoundNumber,
	formatNumber,
} from '@shared/utils/formatNumber';
import isItemPortCall from '@shared/utils/isItemPortCall';
import {
	fromISOTimezone,
	toISOTimezone,
} from '@shared/utils/timezones';
import { Values } from '@shared/utils/objectEnums';
import { qualifiesForEuComplianceCosts } from '@shared/utils/emissionsCompliance/euCompliance/euComplianceCosts';
import { getEstimatedTimeInPort } from '@shared/utils/portCallDays';
import { getPortPurposes, updatePortCall, updateVoyageExpense } from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import LoadingIndicator from '@client/components/LoadingIndicator';
import EditableField from '@client/components/EditableField';
import {
	ItineraryPortCallDto,
	ItinerarySeaPassageDto,
} from '@client/screens/estimates/details/helpers/types';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { useItinerary } from '../ItineraryProvider';
import { getPortPurposeLabel, manuallySelectablePurposes } from '../utils/purposeUtil';
import { getEuEtsApply, getFuelEuApply, updateEuComplianceRowsApply } from '../utils/euComplianceApply';
import { getPortDaysForPortCallEtd, getSetEtdDisabledReason } from '../utils/setEtd';
import SetEtdPopover from '../components/SetEtdPopover';
import {
	normalizePortDateTime,
	PortAwareDatePicker,
	toPortDateTimeInputValue,
} from '../components/PortAwareDatePicker';
import styles from './styles/SummaryTab.module.css';
import EuComplianceApply from './EuComplianceApply';

type OtherConsumptionData = {
	idKey: string;
	daysKey: string;
	idValue: number | null | undefined;
	daysValue: number | null | undefined;
};

type EuComplianceRow = {
	type: 'portCall' | 'seaPassage';
	sourceId: number;
	apply?: boolean;
};

const PortCallDetails = () => {
	const {
		refreshVoyageDetails,
		refreshItinerary,
		expandedEntry,
		itinerary,
	} = useItinerary();

	const {
		vessel,
		vendorExpenses,
		voyageDetails,
		refreshVendorExpenses,
		refreshPnl,
		fixtureType,
	} = useVoyage();

	const {
		data: portPurposeList = [],
	} = useQuery(
		['portPurposes'],
		getPortPurposes,
	);

	const refreshDetails = () => {
		refreshVoyageDetails();
		refreshItinerary();
	};

	const selectedEntry = expandedEntry as ItineraryPortCallDto;
	const [localPortCall, setLocalPortCall] = useState<ItineraryPortCallDto>(selectedEntry);
	const [pendingPortCallChanges, setPendingPortCallChanges] = useState<Partial<ItineraryPortCallDto>>({});
	const [localEuEtsApply, setLocalEuEtsApply] = useState<boolean | null>(null);
	const [localFuelEuApply, setLocalFuelEuApply] = useState<boolean | null>(null);

	useEffect(() => {
		setLocalPortCall(selectedEntry);
		setPendingPortCallChanges({});
		setLocalEuEtsApply(null);
		setLocalFuelEuApply(null);
	}, [selectedEntry]);

	const eligiblePortPurposes = useMemo(() => (
		portPurposeList
			.filter((item) => item.includeInEuComplianceCosts)
			.map((item) => item.purpose)
	), [portPurposeList]);

	const euCompliancePercent = useMemo(() => {
		const qualifies = localPortCall.PortCallActions.some((action) => (
			qualifiesForEuComplianceCosts(
				{
					type: action.event as Values<typeof PortActionTypes>,
					port: localPortCall.port,
				},
				eligiblePortPurposes,
			)
		));

		return qualifies ? 100 : 0;
	}, [localPortCall.PortCallActions, localPortCall.port, eligiblePortPurposes]);

	const isLoadingOrDischarging = localPortCall.PortCallActions
		.some(
			(pca) => pca.event === PortActionTypes.LOADING ||
				pca.event === PortActionTypes.DISCHARGING,
		);

	const {
		arrivalDate,
		departureDate,
		requiredArrivalDate,
		estimatedDepartureDate,
		estimatedArrivalDate,
		port,
		requiredSpeed,
		estimatedIdleDays,
		estimatedLoadingDays,
		estimatedDischargingDays,
		estimatedTurningDays,
		actions,
		mastersEta,
		otherConsumption1Id,
		otherConsumption1Days,
		otherConsumption2Id,
		otherConsumption2Days,
		otherConsumption3Id,
		otherConsumption3Days,
		otherConsumption4Id,
		otherConsumption4Days,
		otherConsumption5Id,
		otherConsumption5Days,
	} = localPortCall;

	const generateOtherDaysMap = (
		otherConsumptionData: Record<string, any>,
	): Record<string, OtherConsumptionData> => {
		return Array.from({ length: 5 }, (_, i) => i + 1).reduce((acc, index) => {
			const idKey = `otherConsumption${index}Id`;
			const daysKey = `otherConsumption${index}Days`;

			acc[idKey] = {
				idKey,
				daysKey,
				idValue: otherConsumptionData[idKey],
				daysValue: otherConsumptionData[daysKey],
			};

			return acc;
		}, {} as Record<string, OtherConsumptionData>);
	};

	const otherConsumptionData = {
		otherConsumption1Id,
		otherConsumption1Days,
		otherConsumption2Id,
		otherConsumption2Days,
		otherConsumption3Id,
		otherConsumption3Days,
		otherConsumption4Id,
		otherConsumption4Days,
		otherConsumption5Id,
		otherConsumption5Days,
	};

	const otherDaysMap = generateOtherDaysMap(otherConsumptionData);

	const vesselOtherConsumptions = vessel?.otherPerformanceEntries;

	const [selectedPort, setSelectedPort] = useState(port);

	const latestActualDate = useMemo(() => {
		if (itinerary == null || itinerary.length === 0) {
			return null;
		}

		let candidate: string | Moment | null = null;
		itinerary.forEach((item) => {
			if (!isItemPortCall(item)) {
				return;
			}

			if (item.departureDate != null) {
				if (
					candidate == null ||
					item.departureDate.isAfter(candidate)
				) {
					candidate = item.departureDate;

					return;
				}
			}

			if (item.arrivalDate != null) {
				if (
					candidate == null ||
					item.arrivalDate.isAfter(candidate)
				) {
					candidate = item.arrivalDate;
				}
			}
		});

		return candidate;
	}, [itinerary]);

	const latestEstimatedDate = useMemo(() => {
		if (expandedEntry == null) {
			return null;
		}

		const prevSeaPassage = itinerary?.find((item): item is ItinerarySeaPassageDto => {
			return !isItemPortCall(item) && item.nextPortCallId === expandedEntry.id;
		});

		if (prevSeaPassage == null) {
			return null;
		}

		const prevPortCall = itinerary?.find(
			(pc): pc is ItineraryPortCallDto => pc.id === prevSeaPassage.prevPortCallId,
		);

		if (prevPortCall == null) {
			return null;
		}

		return prevPortCall.estimatedDepartureDate;
	}, [itinerary, expandedEntry]);

	const isPreviousActual = useMemo(() => {
		if (expandedEntry == null) {
			return false;
		}

		if (!isItemPortCall(expandedEntry)) {
			const prevPortCall = itinerary?.find((item): item is ItineraryPortCallDto => {
				return isItemPortCall(item) && item?.nextSeaPassageId === expandedEntry.id;
			});

			return prevPortCall != null && prevPortCall.arrivalDate != null;
		}

		const prevSeaPassage = itinerary?.find((item): item is ItinerarySeaPassageDto => {
			return !isItemPortCall(item) && item.nextPortCallId === expandedEntry.id;
		});

		if (prevSeaPassage == null) {
			return true;
		}

		const prevPortCall = itinerary?.find((item): item is ItineraryPortCallDto => {
			return isItemPortCall(item) && prevSeaPassage?.prevPortCallId === item.id;
		});

		return prevPortCall?.departureDate != null;
	}, [itinerary, expandedEntry]);

	const isCommencement = actions.some((a) => a.event === PortActionTypes.COMMENCEMENT);
	const hasDelivery = actions.some((a) => a.event === PortActionTypes.DELIVERY);
	const defaultPortTzOffset = useMemo(() => getUtcOffset(selectedPort), [selectedPort]);
	const [saving, setSaving] = useState(false);

	const onUpdatePortCall = async (attributes: Partial<ItineraryPortCallDto>) => {
		setLocalPortCall((prev) => {
			return { ...prev, ...attributes };
		});

		setPendingPortCallChanges((prev) => {
			if (!selectedEntry) {
				return prev;
			}

			const next = { ...prev };

			for (const [key, value] of Object.entries(attributes)) {
				const originalValue = selectedEntry[key as keyof ItineraryPortCallDto];

				if (value === originalValue) {
					// Change was reverted → remove from pending
					delete next[key as keyof ItineraryPortCallDto];
				} else {
					// Actual difference → keep
					next[key as keyof ItineraryPortCallDto] = value as any;
				}
			}

			return next;
		});
	};

	const onSaveChanges = async () => {
		setSaving(true);

		try {
			// @ts-ignore
			await updatePortCall(localPortCall.vesselId, localPortCall.id, pendingPortCallChanges, true);
			await refreshDetails();
			setPendingPortCallChanges({});
		} catch (e) {
			showErrorNotification('Could not update port call', e as Error);
		} finally {
			setSaving(false);
		}
	};

	useEffect(() => {
		setSelectedPort(port);
	}, [port]);

	const requiredSpeedContent = useMemo(() => {
		if (requiredSpeed == null) {
			return (
				<Typography.Text>N/A</Typography.Text>
			);
		}

		if (Number.isNaN(requiredSpeed) || requiredSpeed < 0) {
			return (
				<Alert
					message="There is no possible speed to arrive at the required time"
					className={styles.requiredSpeedAlert}
				/>
			);
		}

		return (
			<Typography.Text>
				{requiredSpeed}
				{' '}
				kt.
			</Typography.Text>
		);
	}, [requiredSpeed]);

	const estimatedTimeInPortContent = useMemo(() => {
		const inPort = round(getEstimatedTimeInPort({
			estimatedIdleDays,
			estimatedLoadingDays,
			estimatedDischargingDays,
			estimatedTurningDays,
		}), 2);

		return (
			<Typography.Text>
				{formatAndRoundNumber(inPort, 2)}
				{' '}
				{inPort === 1 ? 'day' : 'days'}
			</Typography.Text>
		);
	}, [estimatedLoadingDays, estimatedDischargingDays, estimatedIdleDays, estimatedTurningDays]);

	const estimatedConsumptionsContent = useMemo(() => {
		if (localPortCall.estimatedConsumptions == null) {
			return (
				<div>
					N/A
				</div>
			);
		}

		const cons = Object.entries(localPortCall?.estimatedConsumptions);

		return cons.map(([fuelType, { quantity }]) => {
			return (
				<div key={fuelType} className={styles.consumptionsRow}>
					<b style={{ whiteSpace: 'nowrap' }}>{`${fuelType}: `}</b>
					<span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
						{`${formatNumber(quantity, { separateThousands: true }, 2)} MT`}
					</span>
				</div>
			);
		});
	}, [localPortCall]);

	const onSetEtd = (targetEtd: Moment) => {
		const result = getPortDaysForPortCallEtd(localPortCall, targetEtd);

		if (result == null) {
			return null;
		}

		if (result.error != null) {
			return result.error;
		}

		onUpdatePortCall({
			...result.days,
			estimatedDepartureDate: targetEtd,
			manualEstimatedDepartureDate: targetEtd,
		});

		return null;
	};

	const etdContent = (
		<Flex align="center" gap={8}>
			<PortAwareDatePicker
				time
				disabled={latestEstimatedDate != null}
				timezone={port.timezone}
				timeZoneFormat={TimeZoneFormat.LocalTime}
				className={styles.datePicker}
				value={toPortDateTimeInputValue(estimatedDepartureDate, TimeZoneFormat.LocalTime, port.timezone)}
				onChange={(value) => onUpdatePortCall({
					estimatedDepartureDate: normalizePortDateTime(value, TimeZoneFormat.LocalTime, port.timezone),
				})}
			/>
			{latestEstimatedDate != null && (
				<SetEtdPopover
					currentEtd={estimatedDepartureDate}
					timezone={port.timezone}
					disabledReason={getSetEtdDisabledReason(localPortCall, fixtureType)}
					onSet={onSetEtd}
				/>
			)}
		</Flex>
	);

	const saveButton = useSaveButton({
		onSave: onSaveChanges,
		title: 'Save & Calculate',
		loading: saving,
		hasPendingChanges: Object.keys(pendingPortCallChanges).length > 0,
	});

	const onEditChange = (key: string, newValue: any) => {
		if (!newValue || !moment.isMoment(newValue)) {
			return undefined;
		}

		const localZoned = newValue.clone().tz(port.timezone, true);
		const toStore = localZoned.clone().utc();

		return onUpdatePortCall({ [key]: toStore });
	};

	const onPurposeChange = (purposes: string[]) => {
		const nonManualActions = localPortCall.PortCallActions.filter((action) => (
			!manuallySelectablePurposes.some((purpose) => (
				purpose.toLowerCase() === action.event.toLowerCase()
			))
		));

		const manualPurposes = purposes.filter((purpose) => (
			manuallySelectablePurposes.some((manualPurpose) => (
				manualPurpose.toLowerCase() === purpose.toLowerCase()
			))
		));

		const nextActions = [
			...nonManualActions,
			...manualPurposes.map((purpose) => ({
				event: purpose,
				portCallId: localPortCall.id,
				vesselId: localPortCall.vesselId,
				voyageId: localPortCall.voyageId,
				port: localPortCall.port,
				date: localPortCall.arrivalDate ?? localPortCall.estimatedArrivalDate,
				posted: false,
				adjustmentPosted: false,
				shouldUpdateAccountingItem: false,
				cargoId: null,
				estimated: true,
			})),
		];

		onUpdatePortCall({
			PortCallActions: nextActions as any,
		});
	};

	const generatedPurposeOptions = localPortCall.PortCallActions
		.filter((action) => !manuallySelectablePurposes.some((purpose) => (
			purpose.toLowerCase() === action.event.toLowerCase()
		)))
		.filter((action) => (
			action.event.toLowerCase() !== PortActionTypes.ARRIVAL.toLowerCase() &&
			action.event.toLowerCase() !== PortActionTypes.DEPARTURE.toLowerCase()
		))
		.map((action) => ({
			label: getPortPurposeLabel(action.event),
			value: action.event,
			disabled: true,
		}));

	const manualPurposeOptions = manuallySelectablePurposes.map((purpose) => ({
		label: getPortPurposeLabel(purpose),
		value: purpose,
	}));

	const purposeOptions = [
		...generatedPurposeOptions,
		...manualPurposeOptions.filter((manual) => (
			!generatedPurposeOptions.some((generated) => (
				generated.value.toLowerCase() === manual.value.toLowerCase()
			))
		)),
	];

	const selectedPurposes = localPortCall.PortCallActions
		.map((action) => action.event)
		.filter((event) => (
			event.toLowerCase() !== PortActionTypes.ARRIVAL.toLowerCase() &&
			event.toLowerCase() !== PortActionTypes.DEPARTURE.toLowerCase()
		))
		.sort((a, b) => getPortPurposeLabel(a).localeCompare(getPortPurposeLabel(b)));

	const sourceType: EuComplianceRow['type'] = 'portCall';
	const sourceId = localPortCall.id;

	const calculatedEuEtsApply = useMemo(() => {
		const expense = vendorExpenses?.rows.find((item) => item.isEuEts);

		return getEuEtsApply(
			expense?.euCalculationRows as EuComplianceRow[] | undefined,
			sourceType,
			sourceId,
		);
	}, [vendorExpenses, sourceId]);

	const calculatedFuelEuApply = useMemo(() => {
		const expense = vendorExpenses?.rows.find((item) => item.isFuelEuPenalty);

		return getFuelEuApply(
			expense?.fuelEuCalculationRows as EuComplianceRow[] | undefined,
			sourceType,
			sourceId,
		);
	}, [vendorExpenses, sourceId]);

	const euEtsApply = localEuEtsApply ?? calculatedEuEtsApply;
	const fuelEuApply = localFuelEuApply ?? calculatedFuelEuApply;

	const [updatingApply, setUpdatingApply] = useState<'euEts' | 'fuelEu' | null>(null);

	const updateRowsApply = (
		rows: EuComplianceRow[],
		apply: boolean,
	) => updateEuComplianceRowsApply(rows, sourceType, sourceId, apply);

	const onApplyChange = async (
		costType: 'euEts' | 'fuelEu',
		apply: boolean,
	) => {
		setUpdatingApply(costType);

		try {
			if (costType === 'euEts') {
				const expense = vendorExpenses?.rows.find((item) => item.isEuEts);

				if (expense == null) {
					throw new Error('EU ETS expense is unavailable');
				}

				setLocalEuEtsApply(apply);
				await updateVoyageExpense(expense.id, {
					euCalculationRows: updateRowsApply(
						(expense.euCalculationRows ?? []) as EuComplianceRow[],
						apply,
					),
				});
			}

			if (costType === 'fuelEu') {
				const expense = vendorExpenses?.rows.find((item) => item.isFuelEuPenalty);

				if (expense == null) {
					throw new Error('FuelEU penalty expense is unavailable');
				}

				setLocalFuelEuApply(apply);
				await updateVoyageExpense(expense.id, {
					fuelEuCalculationRows: updateRowsApply(
						(expense.fuelEuCalculationRows ?? []) as EuComplianceRow[],
						apply,
					),
				});
			}

			await refreshDetails();
			await refreshVendorExpenses();
			await refreshPnl();
		} catch (e) {
			setLocalEuEtsApply(null);
			setLocalFuelEuApply(null);
			showErrorNotification('Could not update EU compliance apply', e as Error);
		} finally {
			setLocalEuEtsApply(null);
			setLocalFuelEuApply(null);
			setUpdatingApply(null);
			setLocalEuEtsApply(null);
			setLocalFuelEuApply(null);
		}
	};

	if (defaultPortTzOffset == null) {
		return (<LoadingIndicator />);
	}

	return (
		<Card
			extra={saveButton}
			styles={{ body: { overflowY: 'scroll' } }}>
			<Descriptions
				bordered
				size="small"
				column={1}
				className={styles.details}
			>
				<Descriptions.Item
					key="purpose"
					label="Purpose"
				>
					<Select
						mode="multiple"
						value={selectedPurposes}
						options={purposeOptions}
						style={{ width: '100%' }}
						placeholder="Select purposes"
						onChange={onPurposeChange}
						optionLabelProp="label"
					/>
				</Descriptions.Item>
				{!localPortCall.isCanalTransit && (
					<Descriptions.Item
						key="euCompliance"
						label="EU Compliance"
					>
						<div style={{ maxWidth: 575 }}>
							<EuComplianceApply
								percent={euCompliancePercent}
								euEtsApply={euEtsApply}
								fuelEuApply={fuelEuApply}
								euEtsLoading={updatingApply === 'euEts'}
								fuelEuLoading={updatingApply === 'fuelEu'}
								onEuEtsChange={(checked) => onApplyChange('euEts', checked)}
								onFuelEuChange={(checked) => onApplyChange('fuelEu', checked)}
								disabled={euCompliancePercent === 0}
								fuelEuEnabled={voyageDetails.fuelEuEnabled}
							/>
						</div>
					</Descriptions.Item>
				)}
			</Descriptions>
			<Row gutter={[16, 16]}>
				{saving && (
					<div className={styles.loadingOverlay}>
						<LoadingIndicator />
					</div>
				)}
				{!isCommencement && (
					<Col span={24}>
						<Descriptions
							bordered
							size="small"
							column={1}
							title="Arrival"
							className={styles.details}
						>
							<Descriptions.Item
								key="estimatedArrivalDate"
								label="ETA"
							>
								<PortAwareDatePicker
									time
									disabled
									timezone={port.timezone}
									timeZoneFormat={TimeZoneFormat.LocalTime}
									className={styles.datePicker}
									value={toPortDateTimeInputValue(
										estimatedArrivalDate,
										TimeZoneFormat.LocalTime,
										port.timezone,
									)}
								/>
							</Descriptions.Item>
							<Descriptions.Item
								key="mastersEta"
								label="Masters ETA"
							>
								<EditableField
									item={{
										editable: true,
										key: 'mastersEta',
										label: 'Masters ETA',
										type: 'date',
										value: mastersEta == null ? null : fromISOTimezone(
											toMoment(mastersEta).toISOString(true),
											TimeZoneFormat.LocalTime,
											port.timezone,
											TimeZoneMode.WallTime,
										),
										inputProps: {
											allowClear: true,
											time: true,
											showTimezone: true,
											staticTimezonePicker: true,
											disableTimezone: true,
											className: styles.datePicker,
											defaultUtcOffset: mastersEta == null ? undefined :
												getUtcOffset(port, toMoment(mastersEta).toDate() ?? undefined),
										},
									}}
									editChange={(key, picked) => onUpdatePortCall(
										{
											[key]: picked ?
												toISOTimezone(
													picked,
													TimeZoneFormat.LocalTime,
													port.timezone,
													TimeZoneMode.WallTime,
												) :
												null,
										},
									)}
									editing
								/>
							</Descriptions.Item>
							<Descriptions.Item
								key="arrivalDate"
								label="Actual Arrival Date"
							>
								<EditableField
									item={{
										editable: true,
										key: 'arrivalDate',
										label: 'Actual Arrival Date',
										type: 'date',
										value: arrivalDate == null ? null : fromISOTimezone(
											toMoment(arrivalDate).toISOString(true),
											TimeZoneFormat.LocalTime,
											port.timezone,
											TimeZoneMode.WallTime,
										),
										inputProps: {
											disabled: !isPreviousActual && !hasDelivery,
											allowClear: true,
											time: true,
											forceUtcUpdate: true,
											staticTimezonePicker: true,
											className: styles.datePicker,
											defaultUtcOffset: arrivalDate == null ? undefined :
												getUtcOffset(port, toMoment(arrivalDate).toDate() ?? undefined),
										},
									}}
									editChange={(key, picked) => onUpdatePortCall(
										{
											[key]: picked ?
												toISOTimezone(
													picked,
													TimeZoneFormat.LocalTime,
													port.timezone,
													TimeZoneMode.WallTime,
												) :
												null,
										},
									)}
									editing
								/>
							</Descriptions.Item>
							<Descriptions.Item
								key="requiredArrivalDate"
								label="Required Arrival Date"
							>
								<EditableField
									item={{
										editable: true,
										key: 'requiredArrivalDate',
										label: 'Required Arrival Date',
										type: 'date',
										value: requiredArrivalDate ?
											toMoment(requiredArrivalDate).tz(port.timezone) : null,
										inputProps: {
											minDate: isPreviousActual ?
												latestActualDate ?? undefined :
												latestEstimatedDate ?? undefined,
											forceUtcUpdate: false,
											time: true,
											staticTimezonePicker: true,
											className: styles.datePicker,
											defaultUtcOffset: requiredArrivalDate == null ? undefined :
												getUtcOffset(port, toMoment(requiredArrivalDate).toDate() ?? undefined),
										},
									}}
									editChange={onEditChange}
									editing
								/>
							</Descriptions.Item>
							<Descriptions.Item
								key="requiredSpeed"
								label="Required Speed"
							>
								{requiredSpeedContent}
							</Descriptions.Item>
						</Descriptions>
					</Col>
				)}
				<Col span={24}>
					<Descriptions
						bordered
						size="small"
						column={1}
						title="Departure"
						className={styles.details}
					>
						<Descriptions.Item
							key="estimatedDepartureDate"
							label="ETD"
						>
							{etdContent}
						</Descriptions.Item>
						{!isCommencement && (
							<Descriptions.Item
								key="departureDate"
								label="Actual Departure Date"
							>
								<EditableField
									item={{
										editable: true,
										key: 'departureDate',
										label: 'Actual Departure Date',
										type: 'date',
										inputProps: {
											disabled: arrivalDate == null && !isCommencement,
											forceUtcUpdate: false,
											minDate: arrivalDate ?? undefined,
											allowClear: true,
											time: true,
											staticTimezonePicker: true,
											className: styles.datePicker,
											defaultValue: departureDate,
											defaultUtcOffset:
												arrivalDate == null ?
													undefined :
													getUtcOffset(port, toMoment(arrivalDate).toDate() ?? undefined),
										},
										value: departureDate ? toMoment(departureDate).tz(port.timezone) : null,
									}}
									editChange={onEditChange}
									editing
								/>
							</Descriptions.Item>
						)}
					</Descriptions>
				</Col>
				<Col span={24}>
					{!isCommencement && (
						<Descriptions
							bordered
							size="small"
							column={1}
							className={styles.estimatedDetails}
						>
							<Descriptions.Item
								key="estimatedIdleDays"
								label="Est. idle days"
							>
								<EditableField
									item={{
										editable: true,
										key: 'estimatedIdleDays',
										label: 'Est. idle days',
										type: 'number',
										style: { width: '100%' },
										value: estimatedIdleDays,
										inputProps: {
											addonAfter: 'days',
											className: styles.portDaysInput,
										},
									}}
									editChange={(key, newValue) => onUpdatePortCall({ [key]: newValue })}
									editing
								/>
							</Descriptions.Item>
							{vesselOtherConsumptions?.map((oc) => {
								const matchedEntry = Object.values(otherDaysMap)
									.find((odm) => odm.idValue === oc.id);

								// Set keys and values, using defaults when no match is found
								const idKey = matchedEntry?.idKey ?? `otherConsumption${oc.id}Id`;
								const daysKey = matchedEntry?.daysKey ?? `otherConsumption${oc.id}Days`;
								const daysValue = matchedEntry?.daysValue ?? 0;

								const handleEditChange = (key: string, newValue: number) => {
									onUpdatePortCall({
										[daysKey]: newValue,
										[idKey]: oc.id,
									});
								};

								return (
									<Descriptions.Item key={oc.id} label={`Est. ${oc.condition.toLowerCase()}`}>
										<EditableField
											item={{
												editable: true,
												key: daysKey,
												label: `Est. ${oc.condition.toLowerCase()}`,
												type: 'number',
												style: { width: '100%' },
												value: daysValue as number,
												inputProps: {
													addonAfter: 'days',
													className: styles.portDaysInput,
												},
											}}
											editChange={handleEditChange}
											editing
										/>
									</Descriptions.Item>
								);
							})}
							{isLoadingOrDischarging && (
								<>
									<Descriptions.Item
										key="estimatedLoadingDays"
										label="Est. loading days"
									>
										<EditableField
											item={{
												editable: true,
												key: 'estimatedLoadingDays',
												label: 'Est. loading days',
												type: 'number',
												value: estimatedLoadingDays,
												inputProps: { className: styles.portDaysInput },
											}}
											editChange={(key, newValue) => onUpdatePortCall({ [key]: newValue })}
											editing
										/>
									</Descriptions.Item>
									<Descriptions.Item
										key="estimatedDischargingDays"
										label="Est. discharging days"
									>
										<EditableField
											item={{
												editable: true,
												key: 'estimatedDischargingDays',
												label: 'Est. discharging days',
												type: 'number',
												value: estimatedDischargingDays,
												inputProps: { className: styles.portDaysInput },
											}}
											editChange={(key, newValue) => onUpdatePortCall({ [key]: newValue })}
											editing
										/>
									</Descriptions.Item>

								</>
							)}
							<Descriptions.Item
								key="estimatedTurningDays"
								label="Est. turn days"
							>
								<EditableField
									item={{
										editable: true,
										key: 'estimatedTurningDays',
										label: 'Est. turn days',
										type: 'number',
										value: estimatedTurningDays,
										inputProps: {
											addonAfter: 'days',
											className: styles.portDaysInput,
										},
									}}
									editChange={(key, newValue) => onUpdatePortCall({ [key]: newValue })}
									editing
								/>
							</Descriptions.Item>
							<Descriptions.Item
								key="estimatedTimeInPort"
								label="Est. time in port"
							>
								{estimatedTimeInPortContent}
							</Descriptions.Item>
							<Descriptions.Item
								key="estimatedConsumptions"
								label={(
									<Space
										direction="vertical"
										size={0}
									>
										<p className={styles.consumptionsLabel}>Est. consumption</p>
										<p className={styles.sulfurLabel}>
											Max sulfur:
											{localPortCall.port.maxSulfur != null ?
												` ${localPortCall.port.maxSulfur}% - ECA` :
												' N/A'}
										</p>
									</Space>
								)}
							>
								{estimatedConsumptionsContent}
							</Descriptions.Item>
						</Descriptions>
					)}
				</Col>
			</Row>
			<br />
		</Card>
	);
};

export default PortCallDetails;
